import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { Navigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, LayoutTemplate, BookOpen } from "lucide-react";
import WorkoutTemplateForm from "@/components/trainer/WorkoutTemplateForm";
import { Link } from "react-router-dom";
import { getWorkoutImage } from "@/utils/workoutImages";

const difficultyColors = {
  beginner: "bg-green-100 text-green-700",
  intermediate: "bg-yellow-100 text-yellow-700",
  advanced: "bg-red-100 text-red-700",
};

const goalLabels = {
  strength: "Strength", hypertrophy: "Hypertrophy", endurance: "Endurance",
  fat_loss: "Fat Loss", general_fitness: "General Fitness",
};

export default function WorkoutTemplates() {
  const { user } = useAuth();
  const isTrainer = user?.role === "admin" || user?.role === "trainer";
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["workoutTemplates"],
    queryFn: () => base44.entities.WorkoutTemplate.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.WorkoutTemplate.create(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workoutTemplates"] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.WorkoutTemplate.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workoutTemplates"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.WorkoutTemplate.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workoutTemplates"] }),
  });

  if (!isTrainer) return <Navigate to="/" replace />;

  const handleSubmit = (data) => {
    if (editing) {
      updateMutation.mutate({ id: editing.id, data });
    } else {
      createMutation.mutate(data);
    }
    setEditing(null);
  };

  const openEdit = (t) => { setEditing(t); setShowForm(true); };
  const openCreate = () => { setEditing(null); setShowForm(true); };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Workout Templates</h1>
          <p className="text-muted-foreground mt-1">Reusable routines you can assign to any client</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" className="gap-2" asChild>
            <Link to="/exercise-library"><BookOpen className="w-4 h-4" /> Exercise Library</Link>
          </Button>
          <Button className="gap-2" onClick={openCreate}>
            <Plus className="w-4 h-4" /> New Template
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      ) : templates.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <LayoutTemplate className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="font-medium text-muted-foreground">No templates yet</p>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Create reusable workout plans to assign to clients quickly</p>
            <Button onClick={openCreate} className="gap-2"><Plus className="w-4 h-4" /> Create First Template</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {templates.map(t => (
            <Card key={t.id} className="border-0 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              <div className="relative h-24 w-full overflow-hidden bg-slate-900">
                <img
                  src={getWorkoutImage(t)}
                  alt={t.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/50 to-transparent" />
              </div>
              <CardContent className="p-4 pt-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold">{t.name}</p>
                    {t.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{t.description}</p>}
                    <div className="flex gap-1.5 mt-2 flex-wrap">
                      <Badge className={`text-xs ${difficultyColors[t.difficulty]}`}>{t.difficulty}</Badge>
                      <Badge variant="secondary" className="text-xs">{goalLabels[t.goal]}</Badge>
                      <Badge variant="outline" className="text-xs">{(t.exercises || []).length} exercises</Badge>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(t)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => deleteMutation.mutate(t.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <WorkoutTemplateForm
        open={showForm}
        onOpenChange={setShowForm}
        onSubmit={handleSubmit}
        initialData={editing}
      />
    </div>
  );
}